import React, { useEffect } from 'react'
import { SiteHeader } from '../components/site-header'
import { Link, useLocation } from 'react-router-dom'
import { InsightsHero } from '../components/insights/hero-section'
import { EventCategory } from '../lib/analytics'
import { safeTrackEvent } from '../lib/analytics-utils'
import { Button } from '../components/ui/button'
import { METADATA } from '../components/meta/metadata'
import { TestimonialsSection } from '../components/shared/testimonials-section'

export default function InsightsPage() {
  const location = useLocation();
  
  // Set metadata for the page
  useEffect(() => {
    document.title = METADATA.insights.title;
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', METADATA.insights.description);
  }, []);
  
  const handleGetInTouchClick = () => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: 'engagement_insights_get_in_touch',
      context: {
        sourcePage: location.pathname,
        destinationPage: '/contact',
        section: 'page_bottom'
      }
    });
  };
  
  const handleBlogPostClick = (postId: string, postUrl: string) => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: `engagement_insights_blog_${postId}`,
      context: {
        sourcePage: location.pathname,
        externalUrl: postUrl,
        postId: postId
      }
    });
  };

  // Blog post data
  const posts = [
    {
      id: 'from-idea-to-app',
      title: 'From Idea to App with Bolt.new and ChatGPT',
      excerpt: "Product managers and creators: AI is now your fastest route from idea to prototype. Here's how.",
      date: 'January, 2025',
      image: '/images/insights/rate-my-app.jpg',
      url: 'https://www.linkedin.com/pulse/from-idea-app-boltnew-chatgpt-jon-block-abrie/',
      readTime: '5 min read'
    },
    {
      id: 'augmented-quad',
      title: 'The Augmented Quad: The Future of Human-AI Development Teams',
      excerpt: 'The future of development teams is smaller, faster and powered by a new kind of AI-human collaboration.',
      date: 'January, 2025',
      image: '/images/insights/augmented-quad.jpg',
      url: 'https://www.linkedin.com/pulse/augmented-quad-future-human-ai-development-teams-jon-block-ccnne/',
      readTime: '7 min read'
    },
    {
      id: 'meta-engineering',
      title: "Meta-Engineering: Unlocking AI's True Power",
      excerpt: "Want AI that actually helps your business? It's not just about algorithms - it's about structure and context.",
      date: 'February, 2025',
      image: '/images/insights/meta-engineering.jpg',
      url: 'https://www.linkedin.com/pulse/meta-engineering-unlocking-ais-true-power-jon-block-gp7ge/',
      readTime: '6 min read'
    },
    {
      id: 'hiring-pub-quiz',
      title: 'Hiring a Pub Quiz Hero',
      excerpt: 'Your new AI teammate is a genius - but only if you teach them how to help.',
      date: 'February, 2025',
      image: '/images/insights/pub-quiz-hero.jpg',
      url: 'https://www.linkedin.com/pulse/hiring-pub-quiz-hero-jon-block-3jfpe/',
      readTime: '4 min read'
    },
    {
      id: 'ai-coding-paradigms',
      title: 'The Three Paradigm Shifts in AI Coding',
      excerpt: "What happens when product and engineering aren't functions you hire for - but capabilities handled entirely by AI?",
      date: 'March, 2025',
      image: '/images/insights/ai-coding-paradigms.jpg',
      url: 'https://www.linkedin.com/pulse/three-paradigm-shifts-ai-coding-jon-block-oer2e/',
      readTime: '5 min read'
    },
    {
      id: 'career-parkour',
      title: 'Career Parkour',
      excerpt: "What replaces the career ladder in a world shaped by AI? A new model is emerging - fluid, fast-moving and built for momentum, not hierarchy.",
      date: 'April, 2025',
      image: '/images/insights/career-parkour.jpg',
      url: 'https://www.linkedin.com/pulse/career-parkour-jon-block-icqce/',
      readTime: '6 min read'
    },
    {
      id: 'vibe-coding-distraction',
      title: 'Vibe Coding Is a Distraction',
      excerpt: 'Vibe coding is brilliant for prototyping, but it\'s also a trap. Here\'s why AI-enhanced development needs structure, not just instinct.',
      date: 'June, 2025',
      image: '/images/insights/vibe-coding.jpg',
      url: 'https://www.linkedin.com/pulse/vibe-coding-distraction-jon-block-qwl3e/',
      readTime: '5 min read'
    },
    {
      id: 'context-engineers',
      title: 'Context Engineers: The Most Critical New Role (and anyone can apply!)',
      excerpt: 'The most valuable codebase at your company isn\'t written in Python or JavaScript. It\'s written in English.',
      date: 'June, 2025',
      image: '/images/insights/context-engineers.jpg',
      url: 'https://www.linkedin.com/pulse/context-engineers-most-critical-new-role-anyone-can-apply-jon-block-fhj4e/',
      readTime: '6 min read'
    },
    {
      id: 'cognition-alignment',
      title: 'AI vs Human Cognition: Where We Really Stand',
      excerpt: 'AI dazzles with language, pattern recognition and rapid outputs. But when measured against human cognition, its strengths and gaps become clear. Here\'s a practical framework for understanding what AI can (and can\'t) do today.',
      date: 'September, 2025',
      image: '/images/insights/cognition-alignment.jpg',
      url: 'https://www.linkedin.com/pulse/where-does-ai-really-stand-against-human-cognition-jon-block-kcrie/',
      readTime: '7 min read'
    },
    {
      id: 'build-vs-instruct',
      title: 'The Build vs Instruct Decision',
      excerpt: 'Are you building your next platform or instructing it? Software 3.0 means natural language instructions executed by AI agents. Here\'s when to build traditionally and when to instruct.',
      date: 'December, 2025',
      image: '/images/insights/build-vs-instruct.jpg',
      url: 'https://www.linkedin.com/pulse/build-vs-instruct-decision-jon-block-jpd3e/',
      readTime: '8 min read'
    },
    {
      id: 'slow-death-of-saas',
      title: 'The Slow Death of B2B SaaS',
      excerpt: 'The build-versus-buy equation just inverted. When AI can build custom tools in hours, why subscribe to software designed for someone else\'s needs?',
      date: 'February, 2026',
      image: '/images/insights/slow-death-of-saas.jpg',
      url: 'https://www.linkedin.com/pulse/slow-death-b2b-saas-jon-block-h8ume/',
      readTime: '8 min read'
    },
    {
      id: 'knowledge-work-agi',
      title: 'Preparing for Knowledge Work AGI',
      excerpt: 'Knowledge Work AGI isn\'t coming - it\'s here. The barrier isn\'t intelligence. It\'s access: institutional knowledge, instructions, dynamic data and system function. Here\'s where to focus your engineering effort.',
      date: 'March, 2026',
      image: '/images/insights/knowledge-work-agi.png',
      url: 'https://www.linkedin.com/pulse/preparing-knowledge-work-agi-jon-block-byp7e/',
      readTime: '10 min read'
    },
    {
      id: 'agentic-is-not-the-whole-story',
      title: 'Agentic Is Not the Whole Story',
      excerpt: 'Vendor agent templates are a strong starting point, not the finish line. The work that makes AI truly yours - your institutional knowledge, instructions and standards - still belongs to your firm.',
      date: 'May, 2026',
      image: '/images/insights/agentic-is-not-the-whole-story.jpg',
      url: 'https://www.linkedin.com/pulse/agentic-whole-story-jon-block-ryk4e/',
      readTime: '6 min read'
    },
    {
      id: 'a-skill-is-only-as-good',
      title: 'A Skill Is Only as Good as What It Knows',
      excerpt: 'The industry\'s attention has gone to skills, and little to the knowledge they depend on. A skill is a specialised way of calling on knowledge, so the knowledge comes first. Here\'s why a company brain falls short, and where to put the effort.',
      date: 'October, 2026',
      image: '/images/insights/a-skill-is-only-as-good.jpg',
      url: 'https://www.linkedin.com/pulse/skill-only-good-what-knows-jon-block-shjqe/',
      readTime: '6 min read'
    }
  ];

  // Reverse the posts array to show most recent first
  const sortedPosts = [...posts].reverse();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <InsightsHero />

        {/* Blog Posts Section */}
        <section className="py-16 sm:py-20">
          <div className="container max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
              {sortedPosts.map((post) => (
                <a 
                  key={post.id}
                  href={post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-card border border-card hover:border-primary/60 rounded-xl overflow-hidden shadow-sm transition-all hover:shadow-md max-w-md mx-auto w-full"
                  onClick={() => handleBlogPostClick(post.id, post.url)}
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold tracking-tight group-hover:text-primary transition-colors mb-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">{post.excerpt}</p>
                    </div>
                    <div className="flex justify-between items-center text-xs text-muted-foreground pt-2 border-t border-border/40">
                      <span>{post.date}</span>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
        
        {/* Testimonials Section */}
        <TestimonialsSection className="bg-primary/5" />

        {/* CTA Section */}
        <section className="py-20 pb-32">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-semibold tracking-tight mb-6">Want to Learn More?</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Discover how Syllepsis can help transform your organisation with AI-powered solutions
              </p>
              <Button
                onClick={handleGetInTouchClick}
                asChild
                size="lg"
                className="px-8"
              >
                <Link to="/contact">
                  Get in Touch
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
} 